import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LibraryBig, Camera, Boxes, ArrowUpRight } from "lucide-react";

/**
 * Feature spotlight — three deep-dive cards for the newer capabilities
 * that don't get proper weight inside the 10-card Features grid:
 *   1. Catalog Suite        (project boards + lookbooks + search)
 *   2. PhotoRoom            (bulk DSLR → studio HD/4K renders)
 *   3. Multiview + 3D Mesh  (silhouette-locked views → photogrammetric 3D)
 *
 * Style language matches the rest of the landing: matte black, gold
 * accent (#D4AF37), glass-morphism cards, Fraunces display for
 * headlines, subtle framer-motion reveals staggered per card.
 */

const PILLARS = [
  {
    icon: LibraryBig,
    kicker: "Catalog Suite",
    title: "Every render, findable in a heartbeat.",
    body:
      "A private, searchable atelier catalog for every render, every multiview, every CAD kit you generate. Filter by style (Jadau · Polki · Kundan · Meenakari · Temple · Rajwadi), stone, karat and client. Assemble lookbooks in seconds and export them as branded PDFs.",
    bullets: [
      "Per-designer usage tracking",
      "Client-approval boards",
      "PDF lookbook export",
      "Style + stone tagging",
    ],
    accent: "from-amber-500/12",
  },
  {
    icon: Camera,
    kicker: "PhotoRoom",
    title: "DSLR chaos → studio HD, in bulk.",
    body:
      "Drop hundreds of raw pieces from your photography table. PhotoRoom parallel-processes each one into a clean, matte-black studio render in HD 4K or full 8K — same lighting, same crop, same mood — ready for your e-commerce site, catalogue PDF or Instagram feed.",
    bullets: [
      "Parallel bulk touchup",
      "Client-side downsize for 25MB+ DSLR files",
      "HD 4K · 8K PBR tiers",
      "Streaming ZIP bulk download",
    ],
    accent: "from-rose-400/10",
  },
  {
    icon: Boxes,
    kicker: "Multiview + Facetra 3D",
    title: "Sheets and mesh your CAD bench trusts.",
    body:
      "Generate a silhouette-locked 0° / 45° / 90° / 180° technical sheet, then push those views through Facetra 3D to reconstruct a real photogrammetric mesh. Download in GLB, STL, OBJ, FBX, USDZ or 3MF — Standard (4K PBR, 15 credits) or HD (8K PBR, 25 credits).",
    bullets: [
      "Locked-silhouette multiview",
      "GLB · STL · OBJ · FBX · USDZ · 3MF",
      "Wall-thickness cheatsheet included",
      "One-sided shell workflow for temple pieces",
    ],
    accent: "from-emerald-400/10",
  },
];

export default function FeatureSpotlight() {
  return (
    <section
      id="spotlight"
      className="relative py-24 md:py-32 border-t border-white/5"
      data-testid="feature-spotlight-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
              What's new in the studio
            </p>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-white">
              Three pillars, added for the{" "}
              <span className="italic gold-gradient-text">working atelier</span>.
            </h2>
          </div>
          <p className="text-white/55 text-sm md:text-base max-w-md">
            Beyond sketch-to-render — the surface areas we built after living inside our own
            customers' design floors for a year.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.kicker}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65, delay: i * 0.1 }}
                data-testid={`spotlight-${p.kicker.toLowerCase().replace(/\s+/g, "-").replace(/\+/g, "-")}`}
                className="relative glass-card rounded-3xl p-7 md:p-8 overflow-hidden group hover:border-[#D4AF37]/25 transition-colors"
              >
                {/* Subtle accent glow — different hue per pillar for visual variety */}
                <div
                  className={
                    "absolute -top-24 -right-16 w-64 h-64 rounded-full bg-gradient-to-br to-transparent blur-3xl opacity-70 pointer-events-none " +
                    p.accent
                  }
                />

                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37]/15 to-transparent border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:border-[#D4AF37]/60 transition-colors">
                    <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>

                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80 mb-2">
                    {p.kicker}
                  </div>
                  <h3 className="font-display text-2xl md:text-[26px] leading-[1.15] tracking-tight text-white">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm text-white/60 leading-relaxed">{p.body}</p>

                  <ul className="mt-6 space-y-2">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-[13px] text-white/70">
                        <span className="mt-[7px] w-1 h-1 rounded-full bg-[#D4AF37] flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Tertiary CTA row — cheap and clear */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-3 text-[13px]"
        >
          <span className="text-white/45">Want to go deeper?</span>
          <Link
            to="/how-it-works"
            data-testid="spotlight-cta-how-it-works"
            className="inline-flex items-center gap-1.5 text-white/85 hover:text-[#D4AF37] transition"
          >
            The seven-stage workflow <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <span className="text-white/20">·</span>
          <Link
            to="/for-jewellers"
            data-testid="spotlight-cta-for-jewellers"
            className="inline-flex items-center gap-1.5 text-white/85 hover:text-[#D4AF37] transition"
          >
            For ateliers <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <span className="text-white/20">·</span>
          <Link
            to="/glossary"
            data-testid="spotlight-cta-glossary"
            className="inline-flex items-center gap-1.5 text-white/85 hover:text-[#D4AF37] transition"
          >
            Glossary <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
